import React, { useState } from 'react'
import { useAuth } from '@hpanel/web-common/contexts/AuthContext'

/**
 * Plan 18 A3 — SFTP connection info page.
 *
 * Pure read-only display. The panel does NOT echo or store the user's
 * cleartext password; we just remind them they connect with the same
 * password they use for the panel itself.
 *
 * Authentication is brokered through the panel by sftpgo's external-
 * auth hook (POST /api/internal/sftp-auth); the tenant's experience is
 * identical to any normal SFTP server — `sftp <user>@<host>` and type
 * the password.
 *
 * Mental model:
 *   Tenant SFTP write at /home/<user>/<domain>/public/foo.php  →
 *   Apache inside the tenant's container sees foo.php immediately
 *   via the same bind mount, no panel push needed.
 */
export const SFTPInfo: React.FC = () => {
  const { user } = useAuth()
  const host = typeof window !== 'undefined' ? window.location.hostname : 'panel'
  const port = 2022
  const username = user?.username ?? '<your-username>'
  const connectCommand = `sftp -P ${port} ${username}@${host}`
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(connectCommand).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
    }
  }

  return (
    <div data-testid="sftp-info-page" className="space-y-6 max-w-3xl">
      <div>
        <h1
          data-testid="page-title"
          className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100"
        >
          SFTP Access
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Upload, edit, and download files in your home tree over SFTP. Use any
          standard SFTP client (Filezilla, Cyberduck, WinSCP, or the `sftp`
          command line).
        </p>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Connection details
        </h2>
        <dl className="grid grid-cols-1 sm:grid-cols-[140px,1fr] gap-y-2 text-sm">
          <dt className="text-gray-500 dark:text-gray-400">Host</dt>
          <dd
            data-testid="sftp-host"
            className="font-mono text-gray-900 dark:text-gray-100"
          >
            {host}
          </dd>
          <dt className="text-gray-500 dark:text-gray-400">Port</dt>
          <dd
            data-testid="sftp-port"
            className="font-mono text-gray-900 dark:text-gray-100"
          >
            {port}
          </dd>
          <dt className="text-gray-500 dark:text-gray-400">Username</dt>
          <dd
            data-testid="sftp-username"
            className="font-mono text-gray-900 dark:text-gray-100"
          >
            {username}
          </dd>
          <dt className="text-gray-500 dark:text-gray-400">Password</dt>
          <dd className="text-gray-700 dark:text-gray-300">
            Same as your panel password. The panel does not display or store
            passwords in plaintext.
          </dd>
        </dl>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Quick connect
        </h2>
        <div className="flex items-center gap-2">
          <code
            data-testid="sftp-connect-cmd"
            className="flex-1 font-mono text-sm px-3 py-2 rounded bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 overflow-x-auto"
          >
            {connectCommand}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            data-testid="sftp-copy-btn"
            className="text-sm px-3 py-2 rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          What you can do
        </h2>
        <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
          <li>
            Upload to{' '}
            <code className="font-mono">&lt;domain&gt;/public/</code> — files
            are served by your container immediately (no separate "deploy"
            step).
          </li>
          <li>
            Read, edit, and download anything under your home tree.
          </li>
          <li>
            Use the same path layout you see in the File Manager.
          </li>
        </ul>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-5 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          What you can&apos;t do
        </h2>
        <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 space-y-1">
          <li>
            Reach outside your home tree. The server chroots every session to{' '}
            <code className="font-mono">/home/{username}/</code>; you cannot{' '}
            <code className="font-mono">cd /etc</code> or see other tenants&apos;
            files.
          </li>
          <li>
            Use an SSH shell. The account is configured for SFTP only — there
            is no interactive login.
          </li>
        </ul>
      </div>
    </div>
  )
}
